import {
  ACCESS_LEVELS,
  BAYGON_ONLY_STATUSES,
  type AccessLevel,
  type Capability,
  type Id,
  type PermissionMap,
  type Resource,
  type Role,
  type TaskItem,
  type TaskStatus,
} from './types.ts';

/** Poids d'un niveau d'accès, pour les comparaisons cumulatives. */
const LEVEL_WEIGHT: Record<AccessLevel, number> = {
  none: 0,
  read: 1,
  write: 2,
  admin: 3,
};

export function isAccessLevel(value: unknown): value is AccessLevel {
  return typeof value === 'string' && (ACCESS_LEVELS as readonly string[]).includes(value);
}

/** `true` si `granted` couvre au moins `required` (admin ⊇ write ⊇ read). */
export function levelSatisfies(granted: AccessLevel, required: AccessLevel): boolean {
  return LEVEL_WEIGHT[granted] >= LEVEL_WEIGHT[required];
}

export function levelOf(permissions: PermissionMap, resource: Resource): AccessLevel {
  return permissions[resource] ?? 'none';
}

export function can(role: Role, resource: Resource, required: AccessLevel = 'read'): boolean {
  return levelSatisfies(levelOf(role.permissions, resource), required);
}

export function hasCapability(role: Role, capability: Capability): boolean {
  return role.capabilities.includes(capability);
}

/**
 * Un « Baygon » est un rôle qui administre la section : il valide les tâches et
 * gère les utilisateurs. Le cahier des charges réserve les statuts « Validé » et
 * « À modifier » à ce rôle.
 */
export function isBaygon(role: Role): boolean {
  if (isChefUniteRole(role)) return false;
  return can(role, 'admin', 'admin') || hasCapability(role, 'tasks.validate');
}

// ---------------------------------------------------------------------------
// Chef d'Unité : lecture seule sur Staff, accès complet à l'app Parents
// ---------------------------------------------------------------------------

export const CHEF_UNITE_ROLE_ID = 'role_chef_unite';

export function isChefUniteRoleId(roleId: string | null | undefined): boolean {
  return roleId === CHEF_UNITE_ROLE_ID;
}

/**
 * Le verrou est porté par l'identifiant du rôle, pas par sa carte de
 * permissions : un Baygon qui cocherait « write » sur ce rôle par erreur
 * n'ouvrirait aucune écriture pour autant.
 */
export function isChefUniteRole(role: Pick<Role, 'id'> | null | undefined): boolean {
  return isChefUniteRoleId(role?.id);
}

/**
 * Applique le verrou lecture seule à un rôle : tout niveau au-dessus de
 * `read` retombe à `read`, et aucune capacité n'est accordée.
 */
export function effectiveRole(role: Role): Role {
  if (!isChefUniteRole(role)) return role;
  const permissions: PermissionMap = {};
  for (const [resource, level] of Object.entries(role.permissions) as Array<[Resource, AccessLevel]>) {
    if (level !== 'none') permissions[resource] = 'read';
  }
  return { ...role, permissions, capabilities: [] };
}

// ---------------------------------------------------------------------------
// Workflow des tâches
// ---------------------------------------------------------------------------

/**
 * Transitions autorisées du workflow. `to_revise` renvoie la tâche à l'animateur,
 * qui peut la reprendre (`doing`) ou la resoumettre directement (`to_validate`).
 */
const TASK_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  todo: ['doing', 'to_validate'],
  doing: ['todo', 'to_validate'],
  to_validate: ['validated', 'to_revise', 'doing'],
  validated: ['to_revise'],
  to_revise: ['doing', 'to_validate'],
};

export interface TransitionCheck {
  allowed: boolean;
  reason?: string;
}

/**
 * Valide un changement de statut de tâche à la fois sur le graphe de transitions
 * et sur les permissions de l'auteur. Utilisé côté client pour désactiver les
 * actions impossibles, et revérifié côté serveur.
 */
export function canTransitionTask(
  task: Pick<TaskItem, 'status' | 'assigneeIds' | 'createdBy'>,
  next: TaskStatus,
  actor: { id: Id; role: Role },
): TransitionCheck {
  if (task.status === next) return { allowed: true };

  if (!can(actor.role, 'tasks', 'write')) {
    return { allowed: false, reason: "Vous n'avez pas la permission de modifier les tâches." };
  }

  const allowedTargets = TASK_TRANSITIONS[task.status];
  if (!allowedTargets.includes(next)) {
    return {
      allowed: false,
      reason: `Transition impossible : « ${task.status} » → « ${next} ».`,
    };
  }

  if (BAYGON_ONLY_STATUSES.includes(next)) {
    const capability: Capability = next === 'validated' ? 'tasks.validate' : 'tasks.revise';
    if (!hasCapability(actor.role, capability) && !can(actor.role, 'admin', 'admin')) {
      return {
        allowed: false,
        reason: 'Seul un Baygon peut valider ou renvoyer une tâche pour modification.',
      };
    }
  }

  return { allowed: true };
}

/** Statuts atteignables depuis l'état courant pour cet utilisateur. */
export function availableTaskTransitions(
  task: Pick<TaskItem, 'status' | 'assigneeIds' | 'createdBy'>,
  actor: { id: Id; role: Role },
): TaskStatus[] {
  return TASK_TRANSITIONS[task.status].filter(
    (next) => canTransitionTask(task, next, actor).allowed,
  );
}

// ---------------------------------------------------------------------------
// Rôles système livrés avec l'application
// ---------------------------------------------------------------------------

const ALL_READ: PermissionMap = {
  baladins: 'read',
  animateurs: 'read',
  calendar: 'read',
  attendance: 'read',
  tasks: 'read',
  finances: 'read',
  inventaire: 'read',
  uniformes: 'read',
  games: 'read',
  photos: 'read',
  documents: 'read',
  messages: 'read',
};

export interface SystemRoleSeed {
  id: Id;
  name: string;
  description: string;
  permissions: PermissionMap;
  capabilities: Capability[];
  rank: number;
}

/**
 * Rôles créés au premier démarrage. Ils restent entièrement modifiables depuis
 * l'onglet Baygon, sauf leur suppression (`isSystem`).
 */
export const SYSTEM_ROLES: SystemRoleSeed[] = [
  {
    id: 'role_baygon',
    name: 'Baygon',
    description: 'Responsable de section. Accès complet, validation des tâches, administration.',
    permissions: {
      baladins: 'admin',
      animateurs: 'admin',
      calendar: 'admin',
      attendance: 'admin',
      tasks: 'admin',
      finances: 'admin',
      games: 'admin',
      inventaire: 'admin',
      uniformes: 'admin',
      photos: 'admin',
      documents: 'admin',
      messages: 'admin',
      admin: 'admin',
      settings: 'admin',
    },
    capabilities: [
      'tasks.validate',
      'tasks.revise',
      'photos.publish',
      'messages.publish',
      'finances.export',
      'attendance.export',
      'trash.restore',
      'backup.manage',
      'categories.manage',
    ],
    rank: 0,
  },
  {
    id: 'role_intendant',
    name: 'Intendant',
    description: 'Animateur avec la gestion des finances et du matériel.',
    permissions: {
      ...ALL_READ,
      calendar: 'write',
      attendance: 'write',
      tasks: 'write',
      finances: 'admin',
      games: 'write',
      inventaire: 'admin',
      uniformes: 'admin',
      photos: 'write',
      documents: 'write',
      messages: 'write',
    },
    capabilities: ['finances.export', 'attendance.export', 'photos.publish'],
    rank: 1,
  },
  {
    id: 'role_animateur',
    name: 'Animateur',
    description: 'Animateur de la section : encode les présences, prépare les réunions.',
    permissions: {
      ...ALL_READ,
      calendar: 'write',
      attendance: 'write',
      tasks: 'write',
      games: 'write',
      inventaire: 'write',
      uniformes: 'write',
      photos: 'write',
      documents: 'write',
      messages: 'write',
      finances: 'read',
    },
    capabilities: ['attendance.export'],
    rank: 2,
  },
  {
    id: 'role_lecture',
    name: 'Lecture seule',
    description: 'Consultation uniquement. Utile pour un animateur en formation ou un cadre d’unité.',
    permissions: ALL_READ,
    capabilities: [],
    rank: 3,
  },
  {
    id: CHEF_UNITE_ROLE_ID,
    name: "Chef d'Unité",
    description:
      "Staff d'Unité : consultation de toute l'application Staff sans aucune modification, et accès complet à l'application Parents.",
    permissions: { ...ALL_READ, settings: 'read' },
    capabilities: [],
    rank: 4,
  },
];

export const DEFAULT_ROLE_ID = 'role_animateur';
export const BAYGON_ROLE_ID = 'role_baygon';

